import axios from "axios";

import { useCallback, useState } from "react";

import { getEnvironment } from "@/shared/config/env";

import { useCreateReleaseWithProgress } from "../hooks/useInstaller";

interface CreateReleaseModalProps {
  onClose: () => void;
}

const environmentLabels: Record<"dev" | "staging" | "production", string> = {
  dev: "Dev",

  staging: "Staging",

  production: "Production",
};

const CreateReleaseModal = ({ onClose }: CreateReleaseModalProps) => {
  const [version, setVersion] = useState("");

  const [releaseNotes, setReleaseNotes] = useState("");

  const [platform, setPlatform] = useState<"windows" | "macos" | "linux">(
    "windows",
  );

  const [environment, setEnvironment] = useState<
    "dev" | "staging" | "production"
  >(getEnvironment());

  const [installerFile, setInstallerFile] = useState<File | null>(null);

  const [updatePackage, setUpdatePackage] = useState<File | null>(null);

  const [error, setError] = useState("");

  const [isDraggingInstaller, setIsDraggingInstaller] = useState(false);

  const [isDraggingPackage, setIsDraggingPackage] = useState(false);

  const createRelease = useCreateReleaseWithProgress();

  const handleDragEnter = useCallback(
    (e: React.DragEvent, type: "installer" | "package") => {
      e.preventDefault();

      e.stopPropagation();

      type === "installer"
        ? setIsDraggingInstaller(true)
        : setIsDraggingPackage(true);
    },

    [],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent, type: "installer" | "package") => {
      e.preventDefault();

      e.stopPropagation();

      type === "installer"
        ? setIsDraggingInstaller(false)
        : setIsDraggingPackage(false);
    },

    [],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();

    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, type: "installer" | "package") => {
      e.preventDefault();

      e.stopPropagation();

      type === "installer"
        ? setIsDraggingInstaller(false)
        : setIsDraggingPackage(false);

      const droppedFile = e.dataTransfer.files[0];

      if (droppedFile) {
        if (type === "installer") {
          setInstallerFile(droppedFile);
        } else {
          setUpdatePackage(droppedFile);
        }

        setError("");
      }
    },

    [],
  );

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,

    type: "installer" | "package",
  ) => {
    const selectedFile = e.target.files?.[0];

    if (selectedFile) {
      if (type === "installer") {
        setInstallerFile(selectedFile);
      } else {
        setUpdatePackage(selectedFile);
      }

      setError("");
    }
  };

  const removeInstallerFile = () => setInstallerFile(null);

  const removeUpdatePackage = () => setUpdatePackage(null);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;

    const sizes = ["Bytes", "KB", "MB", "GB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError("");

    if (!version.trim()) {
      setError("La versión es requerida");

      return;
    }

    if (!installerFile) {
      setError("Debes seleccionar el instalador (.exe)");

      return;
    }

    if (!updatePackage) {
      setError("Debes seleccionar el paquete de actualización (.zip)");

      return;
    }

    try {
      await createRelease.mutateAsync({
        version,

        releaseNotes,

        platform,

        environment,

        installerFile,

        updatePackage,
      });

      onClose();
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const serverMessage = err.response?.data?.message;

        if (serverMessage) {
          setError(serverMessage);
        } else if (err.code === "ECONNABORTED") {
          setError("La subida tardó demasiado tiempo. Intenta nuevamente.");
        } else if (err.code === "ERR_NETWORK") {
          setError("Error de conexión. Verifica tu conexión a internet.");
        } else if (err.response?.status === 413) {
          setError("El archivo es demasiado grande.");
        } else if (err.response?.status && err.response.status >= 500) {
          setError("Error del servidor. Intenta más tarde.");
        } else {
          setError(err.message || "Error al crear la versión");
        }
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Error al crear la versión");
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-4xl rounded-lg border border-slate-700 bg-surface p-6 shadow-xl">
        <h2 className="mb-4 text-xl font-semibold text-slate-100">
          Nueva Versión del Instalador
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">
              Versión *
            </label>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="1.0.0"
              className="w-full rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-200 outline-none transition focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">
              Plataforma *
            </label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as typeof platform)}
              className="w-full rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-200 outline-none transition focus:border-primary"
            >
              <option value="windows">Windows</option>
              <option value="macos">macOS</option>
              <option value="linux">Linux</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">
              Entorno *
            </label>
            <select
              value={environment}
              onChange={(e) =>
                setEnvironment(e.target.value as typeof environment)
              }
              className="w-full rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-200 outline-none transition focus:border-primary"
            >
              <option value="dev">Dev</option>
              <option value="staging">Staging</option>
              <option value="production">Production</option>
            </select>
          </div>

          <div
            className={
              environment === "production"
                ? "rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-300"
                : "rounded-md border border-slate-700 bg-background px-4 py-2 text-sm text-slate-300"
            }
          >
            Publicando en:{" "}
            <span className="font-semibold">
              {environmentLabels[environment]}
            </span>
            {environment === "production" && (
              <span className="mt-1 block text-xs text-amber-400/80">
                Esta versión quedará disponible para los usuarios finales al
                publicarla.
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="flex flex-col">
              <label className="mb-2 block text-sm font-medium text-slate-300">
                Archivo del instalador (EXE) *
              </label>
              <div
                onDragEnter={(e) => handleDragEnter(e, "installer")}
                onDragOver={handleDragOver}
                onDragLeave={(e) => handleDragLeave(e, "installer")}
                onDrop={(e) => handleDrop(e, "installer")}
                className={`relative rounded-lg border-2 border-dashed p-8 text-center transition ${
                  isDraggingInstaller
                    ? "border-primary bg-primary/10"
                    : "border-slate-700 bg-surface hover:border-slate-600"
                }`}
              >
                <input
                  type="file"
                  accept=".exe,.dmg,.deb,.rpm,.AppImage"
                  onChange={(e) => handleFileSelect(e, "installer")}
                  className="absolute inset-0 cursor-pointer opacity-0"
                  disabled={createRelease.isPending}
                />
                <div className="flex flex-col items-center gap-2">
                  <svg
                    className="h-12 w-12 text-slate-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-sm text-slate-300">
                    Arrastra el archivo aquí o{" "}
                    <span className="text-primary">
                      haz clic para seleccionar
                    </span>
                  </p>
                  <p className="text-xs text-slate-500">
                    Archivos soportados: .exe, .dmg, .deb, .rpm, .AppImage
                  </p>
                </div>
              </div>

              {installerFile && (
                <div className="mt-4">
                  <div className="flex items-center justify-between rounded-md border border-slate-700 bg-surface p-3">
                    <div className="flex items-center gap-3">
                      <svg
                        className="h-5 w-5 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-slate-200">
                          {installerFile.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatFileSize(installerFile.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeInstallerFile}
                      className="text-red-400 transition hover:text-red-300"
                      disabled={createRelease.isPending}
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col">
              <label className="mb-2 block text-sm font-medium text-slate-300">
                Paquete de actualización (ZIP) *
              </label>
              <div
                onDragEnter={(e) => handleDragEnter(e, "package")}
                onDragOver={handleDragOver}
                onDragLeave={(e) => handleDragLeave(e, "package")}
                onDrop={(e) => handleDrop(e, "package")}
                className={`relative rounded-lg border-2 border-dashed p-8 text-center transition ${
                  isDraggingPackage
                    ? "border-primary bg-primary/10"
                    : "border-slate-700 bg-surface hover:border-slate-600"
                }`}
              >
                <input
                  type="file"
                  accept=".zip"
                  onChange={(e) => handleFileSelect(e, "package")}
                  className="absolute inset-0 cursor-pointer opacity-0"
                  disabled={createRelease.isPending}
                />
                <div className="flex flex-col items-center gap-2">
                  <svg
                    className="h-12 w-12 text-slate-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-sm text-slate-300">
                    Arrastra el archivo aquí o{" "}
                    <span className="text-primary">
                      haz clic para seleccionar
                    </span>
                  </p>
                  <p className="text-xs text-slate-500">
                    Archivo soportado: .zip (máx. 500MB)
                  </p>
                </div>
              </div>

              {updatePackage && (
                <div className="mt-4">
                  <div className="flex items-center justify-between rounded-md border border-slate-700 bg-surface p-3">
                    <div className="flex items-center gap-3">
                      <svg
                        className="h-5 w-5 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-slate-200">
                          {updatePackage.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatFileSize(updatePackage.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeUpdatePackage}
                      className="text-red-400 transition hover:text-red-300"
                      disabled={createRelease.isPending}
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">
              Notas de la versión
            </label>
            <textarea
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
              placeholder="Describe los cambios en esta versión..."
              rows={4}
              className="w-full rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-200 outline-none transition focus:border-primary"
            />
          </div>

          {createRelease.isPending && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300">Subiendo archivos...</span>
                <span className="text-primary">
                  {createRelease.uploadProgress}%
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-slate-700">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-primary to-primaryAccent "
                  style={{ width: `${createRelease.uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {error ? (
            <div className="rounded-md bg-red-900/30 px-4 py-2 text-sm text-red-400">
              {error}
            </div>
          ) : null}

          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={createRelease.isPending}
              className="rounded-md border border-slate-700 bg-background px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-slate-600 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createRelease.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primaryAccent disabled:opacity-50"
            >
              {createRelease.isPending ? "Creando..." : "Crear Versión"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateReleaseModal;
